import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gift, Check, X, Loader2, Clock, Building2, AlertCircle,
  Eye, ChevronDown, ChevronUp, CheckCircle2, XCircle, Image as ImageIcon,
  FileText, ArrowLeft, Filter, RefreshCw, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const STATUS_FILTERS = [
  { id: 'pending', label: 'Pendientes', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  { id: 'approved', label: 'Aprobados', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { id: 'rejected', label: 'Rechazados', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

const BenefitApprovalPage = () => {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    fetchBenefits();
  }, [statusFilter]);

  const fetchBenefits = async () => {
    setLoading(true);
    try {
      const endpoint = statusFilter === 'pending' 
        ? '/partners/admin/benefits/pending'
        : `/partners/admin/benefits/all?status=${statusFilter}`;
      const response = await api.get(endpoint);
      setBenefits(response.data);
    } catch (error) {
      console.error('Error fetching benefits:', error);
      // If endpoint doesn't exist for filtered, just show pending
      if (statusFilter !== 'pending') {
        setStatusFilter('pending');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (benefitId) => {
    setActionLoading(benefitId);
    try {
      await api.post(`/partners/admin/benefits/${benefitId}/approve`);
      toast.success('Beneficio aprobado');
      setBenefits(prev => prev.filter(b => b.id !== benefitId));
    } catch (error) {
      toast.error('Error al aprobar beneficio');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (benefit) => {
    setSelectedBenefit(benefit);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const openPreviewModal = (benefit) => {
    setSelectedBenefit(benefit);
    setShowPreviewModal(true);
  };

  const handleReject = async () => {
    if (!selectedBenefit) return;
    
    setActionLoading(selectedBenefit.id);
    try {
      await api.post(`/partners/admin/benefits/${selectedBenefit.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Beneficio rechazado');
      setBenefits(prev => prev.filter(b => b.id !== selectedBenefit.id));
      setShowRejectModal(false);
      setSelectedBenefit(null);
    } catch (error) {
      toast.error('Error al rechazar beneficio');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (benefitId) => {
    if (!window.confirm('¿Estás seguro de eliminar este beneficio permanentemente? Esta acción no se puede deshacer.')) {
      return;
    }
    
    setActionLoading(benefitId);
    try {
      await api.delete(`/partners/admin/benefits/${benefitId}`);
      toast.success('Beneficio eliminado');
      setBenefits(prev => prev.filter(b => b.id !== benefitId));
    } catch (error) {
      toast.error('Error al eliminar beneficio');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400'
    };
    const labels = { pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 lg:p-6 space-y-6"
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/partners" className="text-[#52525B] hover:text-white transition-colors">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                  <Gift className="text-[#D4AF37]" size={28} />
                  Aprobar Beneficios
                </h1>
                <p className="text-[#71717A] text-sm mt-1">Revisa los beneficios creados por partners</p>
              </div>
            </div>
            <Button
              onClick={fetchBenefits}
              variant="outline"
              className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#52525B]"
            >
              <RefreshCw size={16} className="mr-2" />
              Actualizar
            </Button>
          </div>

          {/* Stats & Filter */}
          <div className="flex flex-wrap items-center gap-3">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setStatusFilter(filter.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  statusFilter === filter.id
                    ? filter.color
                    : 'bg-[#121212] border-[#27272A] text-[#71717A] hover:text-white hover:border-[#52525B]'
                }`}
              >
                {filter.label}
                {filter.id === 'pending' && benefits.length > 0 && statusFilter === 'pending' && (
                  <span className="ml-2 px-1.5 py-0.5 bg-black/30 rounded-full text-xs">{benefits.length}</span>
                )}
              </button>
            ))}
          </div>

          {/* Benefits List */}
          <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
              </div>
            ) : benefits.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 mx-auto bg-green-500/10 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 size={32} className="text-green-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {statusFilter === 'pending' ? '¡Todo al día!' : 'Sin resultados'}
                </h3>
                <p className="text-[#71717A]">
                  {statusFilter === 'pending' 
                    ? 'No hay beneficios pendientes de aprobación' 
                    : `No hay beneficios ${statusFilter === 'approved' ? 'aprobados' : 'rechazados'}`}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[#27272A]">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-5 hover:bg-[#18181B] transition-colors"
                  >
                    <div className="flex gap-4">
                      {/* Image */}
                      <div className="flex-shrink-0">
                        {benefit.image_url ? (
                          <img 
                            src={benefit.image_url} 
                            alt=""
                            className="w-24 h-24 rounded-xl object-cover bg-[#0A0A0A] cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => openPreviewModal(benefit)}
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-xl bg-[#0A0A0A] flex items-center justify-center">
                            <ImageIcon size={32} className="text-[#27272A]" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 flex-wrap">
                              <h3 className="font-bold text-white text-lg">{benefit.title}</h3>
                              {getStatusBadge(benefit.status)}
                            </div>
                            <div className="flex items-center gap-2 text-[#71717A] text-sm mt-1">
                              <Building2 size={14} />
                              <span>{benefit.partner_name || 'Partner'}</span>
                              <span className="text-[#3F3F46]">•</span>
                              <span className="px-2 py-0.5 bg-[#27272A] rounded text-xs">{benefit.category || 'General'}</span>
                            </div>
                          </div>
                        </div>

                        <p className="text-[#A1A1AA] text-sm mt-3 line-clamp-2">{benefit.description}</p>

                        {benefit.terms && (
                          <div className="mt-3 p-3 bg-[#0A0A0A] rounded-lg">
                            <p className="text-xs text-[#52525B] uppercase tracking-wider mb-1">Términos y Condiciones</p>
                            <p className="text-[#71717A] text-sm line-clamp-2">{benefit.terms}</p>
                          </div>
                        )}

                        {/* Meta & Actions */}
                        <div className="flex items-center justify-between mt-4">
                          <span className="text-[#52525B] text-xs">
                            Creado {new Date(benefit.created_at).toLocaleDateString('es-AR')}
                          </span>
                          
                          {statusFilter === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => openPreviewModal(benefit)}
                                className="bg-[#27272A] text-white hover:bg-[#3F3F46]"
                              >
                                <Eye size={14} className="mr-1" />
                                Ver
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openRejectModal(benefit)}
                                disabled={actionLoading === benefit.id}
                                className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                              >
                                <X size={14} className="mr-1" />
                                Rechazar
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleApprove(benefit.id)}
                                disabled={actionLoading === benefit.id}
                                className="bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30"
                              >
                                {actionLoading === benefit.id ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <>
                                    <Check size={14} className="mr-1" />
                                    Aprobar
                                  </>
                                )}
                              </Button>
                            </div>
                          )}

                          {statusFilter === 'approved' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => openPreviewModal(benefit)}
                                className="bg-[#27272A] text-white hover:bg-[#3F3F46]"
                              >
                                <Eye size={14} className="mr-1" />
                                Ver
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleDelete(benefit.id)}
                                disabled={actionLoading === benefit.id}
                                className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                              >
                                {actionLoading === benefit.id ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <>
                                    <Trash2 size={14} className="mr-1" />
                                    Eliminar
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Preview Modal */}
          <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
            <DialogContent className="bg-[#121212] border border-[#27272A] max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
                  <Eye size={20} className="text-[#D4AF37]" />
                  Vista Previa del Beneficio
                </DialogTitle>
              </DialogHeader>
              
              {selectedBenefit && (
                <div className="space-y-4 mt-4">
                  {selectedBenefit.image_url && (
                    <img 
                      src={selectedBenefit.image_url} 
                      alt={selectedBenefit.title}
                      className="w-full h-48 object-cover rounded-xl"
                    />
                  )}
                  
                  <div>
                    <h3 className="text-2xl font-bold text-white">{selectedBenefit.title}</h3>
                    <p className="text-[#71717A] mt-1">Por {selectedBenefit.partner_name}</p>
                  </div>

                  <p className="text-[#A1A1AA]">{selectedBenefit.description}</p>

                  {selectedBenefit.terms && (
                    <div className="p-4 bg-[#0A0A0A] rounded-xl">
                      <p className="text-xs text-[#52525B] uppercase tracking-wider mb-2">Términos y Condiciones</p>
                      <p className="text-white text-sm">{selectedBenefit.terms}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-[#0A0A0A] rounded-lg">
                      <p className="text-[#52525B]">Categoría</p>
                      <p className="text-white font-medium">{selectedBenefit.category || 'General'}</p>
                    </div>
                    <div className="p-3 bg-[#0A0A0A] rounded-lg">
                      <p className="text-[#52525B]">Fecha</p>
                      <p className="text-white font-medium">{new Date(selectedBenefit.created_at).toLocaleDateString('es-AR')}</p>
                    </div>
                  </div>

                  {statusFilter === 'pending' && (
                    <div className="flex gap-3 pt-4 border-t border-[#27272A]">
                      <Button
                        onClick={() => { openRejectModal(selectedBenefit); setShowPreviewModal(false); }}
                        className="flex-1 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                      >
                        <XCircle size={16} className="mr-2" />
                        Rechazar
                      </Button>
                      <Button
                        onClick={() => { handleApprove(selectedBenefit.id); setShowPreviewModal(false); }}
                        disabled={actionLoading === selectedBenefit.id}
                        className="flex-1 bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30"
                      >
                        <CheckCircle2 size={16} className="mr-2" />
                        Aprobar
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Reject Modal */}
          <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
            <DialogContent className="bg-[#121212] border border-[#27272A] max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
                  <AlertCircle size={20} className="text-red-500" />
                  Rechazar Beneficio
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <p className="text-[#A1A1AA]">
                  Estás por rechazar <strong className="text-white">{selectedBenefit?.title}</strong> de <strong className="text-white">{selectedBenefit?.partner_name}</strong>.
                </p>

                <div>
                  <label className="text-sm font-medium text-[#A1A1AA] block mb-2">
                    Motivo del rechazo (opcional)
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Ej: La descripción no es clara..."
                    rows={3}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#27272A] rounded-xl text-white placeholder-[#52525B] focus:border-red-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="ghost" onClick={() => setShowRejectModal(false)} className="text-[#A1A1AA]">
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleReject}
                    disabled={actionLoading === selectedBenefit?.id}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {actionLoading === selectedBenefit?.id ? (
                      <Loader2 size={16} className="animate-spin mr-2" />
                    ) : (
                      <XCircle size={16} className="mr-2" />
                    )}
                    Rechazar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default BenefitApprovalPage;
